import { SelectChangeEvent } from '@mui/material'
import { action, computed, makeObservable, observable, toJS } from 'mobx'
import { ChangeEvent } from 'react'
import { ISubFormStore } from './interfaces'

export type FieldStoreProps<T> = {
  value: T
  onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => void
  error: boolean
  onBlur: () => void
}

export class FieldStore<T> implements ISubFormStore<T> {
  initialValue: T
  value: T
  isTouched = false
  error?: string

  constructor(
    value: T,
    private validator?: (val: T) => string | undefined,
  ) {
    this.value = value
    this.initialValue = value
    makeObservable(this, {
      value: observable,
      isTouched: observable,
      error: observable,
      setValue: action,
      touch: action,
      unTouch: action,
      validate: action,
      clear: action,
      setError: action,
      updateInitialValue: action,
      dirty: computed,
      props: computed,
    })
  }

  onChange = (event?: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
    if (!event) {
      this.setValue(this.initialValue)
    } else if (typeof this.initialValue === 'number') {
      const parsedValue = Number(event.target.value)
      const isNumber = !isNaN(parsedValue) && event.target.value.trim() !== ''

      if (isNumber) {
        this.setValue(parsedValue as T)
      } else {
        this.setValue(undefined as T)
      }
    } else this.setValue(event.target.value as T)
  }

  setValue = (value: T) => {
    this.value = value
    this.error = undefined
  }

  updateInitialValue = (value: T) => {
    this.initialValue = value
    this.unTouch()
    this.setError()
  }

  setError = (error?: string) => {
    this.error = error
  }

  get dirty() {
    if (Array.isArray(this.value) && Array.isArray(this.initialValue)) {
      return JSON.stringify(this.value) !== JSON.stringify(this.initialValue)
    }

    return this.value !== this.initialValue
  }

  touch = () => {
    this.isTouched = true
  }

  unTouch = () => {
    this.isTouched = false
  }

  validate = () => {
    if (this.validator) {
      const error = this.validator(this.value)
      this.error = error
      return error === undefined
    }
    return true
  }

  clear = () => {
    this.value = this.initialValue
    this.error = undefined
    this.isTouched = false
  }

  get data() {
    return toJS(this.value)
  }

  get props(): FieldStoreProps<T> {
    return {
      value: this.value,
      onChange: this.onChange,
      error: !!this.error,
      onBlur: () => this.touch(),
    }
  }
}
